import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLogin } from '@/hooks/useAuth'
import { validateLoginForm } from '@/utils/validation'
import { LoginFormData } from '@/types'
import { Eye, EyeOff, Mail, Lock, AlertCircle, ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'

interface LoginFormProps {
  onSuccess?: () => void
  onError?: (error: string) => void
  className?: string
}

/**
 * Login Form Component
 * Handles user authentication with email and password
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onError, className = '' }) => {
  const navigate = useNavigate()
  const { login, isLoading } = useLogin()
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    const validation = validateLoginForm(formData)
    if (!validation.isValid) {
      setErrors(validation.errors)
      return
    }

    // Clear previous errors
    setErrors({})

    // Attempt login
    const result = await login(formData)
    if (result.success) {
      onSuccess?.()
      navigate('/dashboard')
    } else {
      const errorMessage = (result.error as any)?.response?.data?.message || 'Login failed'
      setErrors({ general: errorMessage })
      onError?.(errorMessage)
    }
  }

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  }

  return (
    <motion.div
      className={`w-full max-w-md ${className}`}
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.form
        onSubmit={handleSubmit}
        className="space-y-6 p-8 bg-slate-800/80 backdrop-blur-sm border border-gray-700/50 rounded-2xl shadow-2xl"
        variants={fadeInUp}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={fadeInUp}>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
          <p className="text-gray-300">Sign in to your PexiBlock account</p>
        </motion.div>

        {/* General Error */}
        {errors.general && (
          <motion.div
            className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-start space-x-3"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <AlertCircle className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-orange-300">{errors.general}</div>
          </motion.div>
        )}

        {/* Email Field */}
        <motion.div className="space-y-2" variants={fadeInUp}>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-300">
            Email Address
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-orange-400 transition-colors" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pl-12 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.email ? 'border-orange-500 focus:ring-orange-500' : 'hover:border-gray-500'}`}
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>
          {errors.email && (
            <motion.p
              className="text-sm text-orange-400 flex items-center space-x-1"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AlertCircle className="h-4 w-4" />
              <span>{errors.email}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Password Field */}
        <motion.div className="space-y-2" variants={fadeInUp}>
          <label htmlFor="password" className="block text-sm font-semibold text-gray-300">
            Password
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-orange-400 transition-colors" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pl-12 pr-12 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${errors.password ? 'border-orange-500 focus:ring-orange-500' : 'hover:border-gray-500'}`}
              placeholder="Enter your password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-4 flex items-center hover:bg-gray-600/50 rounded-r-xl transition-colors"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-orange-400 transition-colors" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-orange-400 transition-colors" />
              )}
            </button>
          </div>
          {errors.password && (
            <motion.p
              className="text-sm text-orange-400 flex items-center space-x-1"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AlertCircle className="h-4 w-4" />
              <span>{errors.password}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Remember Me & Forgot Password */}
        <motion.div className="flex items-center justify-between" variants={fadeInUp}>
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-600 bg-gray-700 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
              Remember me
            </label>
          </div>
          <a
            href="/forgot-password"
            className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors"
          >
            Forgot password?
          </a>
        </motion.div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading}
          className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-slate-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          variants={fadeInUp}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Signing in...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              Sign In
              <ArrowRight className="w-5 h-5 ml-2" />
            </div>
          )}
        </motion.button>

        {/* Contact Admin Link */}
        <motion.div className="text-center" variants={fadeInUp}>
          <p className="text-sm text-gray-300">
            Need an account?{' '}
            <a
              href="mailto:admin@pexiblocks.com"
              className="font-semibold text-orange-400 hover:text-orange-300 transition-colors"
            >
              Contact Administrator
            </a>
          </p>
        </motion.div>
      </motion.form>
    </motion.div>
  )
}
